import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import JSZip from 'jszip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminCourseName } from '../utils/courseDisplay';

type ScormVersion = '1.2' | '2004';

interface CourseResponse {
  id: string;
  courseProject?: string;
  title: string | Record<string, string>;
  description?: string | Record<string, string>;
  image?: string;
  availableLanguages?: string[];
  defaultLanguage?: string;
}

interface ScormExportItem {
  id: string;
  scormVersion: ScormVersion;
  active: boolean;
  tokenExpiresAt?: string;
  exportVersion: string;
  exportedAt: string;
  clientId: string;
  createdAt: string;
  uniqueLearnersCount?: number;
  freeEnrollment?: boolean;
  emailOptional?: boolean;
  enrollmentGroupId?: string | null;
  enrollmentGroupName?: string | null;
  company?: {
    id: string;
    name: string;
    slug: string;
  } | null;
}

interface GroupOption {
  id: string;
  name: string;
}

interface CreateExportResponse {
  export: {
    id: string;
    scormVersion: ScormVersion;
    active: boolean;
    tokenExpiresAt?: string;
    exportVersion: string;
    exportedAt: string;
    clientId: string;
    companyId?: string | null;
    companySlug?: string | null;
    token: string;
  };
}

/** Champs nécessaires pour générer le ZIP (création ou retéléchargement). */
type ScormZipPayload = {
  token: string;
  clientId: string;
  scormVersion: ScormVersion;
  exportVersion: string;
  tokenExpiresAt?: string | null;
  companySlug?: string | null;
};

interface CompanyOption {
  id: string;
  name: string;
  slug: string;
}

const resolveTitle = (title: string | Record<string, string>, defaultLanguage = 'en'): string => {
  if (typeof title === 'string') return title;
  return title[defaultLanguage] || title.en || Object.values(title)[0] || 'course';
};

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

const toDatetimeLocal = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const escapeXml = (input: string): string =>
  input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const getLocalized = (
  value: string | Record<string, string> | undefined,
  defaultLanguage: string,
  fallback = ''
): string => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value[defaultLanguage] || value.fr || value.en || Object.values(value)[0] || fallback;
};

const buildLomMetadata = ({
  title,
  description,
  defaultLanguage,
  availableLanguages,
  schemaversion,
  thumbnailPath,
}: {
  title: string;
  description: string;
  defaultLanguage: string;
  availableLanguages: string[];
  schemaversion: string;
  thumbnailPath?: string;
}): string => {
  const safeTitle = escapeXml(title);
  const safeDescription = escapeXml(description || title);
  const languages = Array.from(
    new Set(
      [defaultLanguage, ...(availableLanguages || [])]
        .map((lang) => (lang || '').trim().toLowerCase())
        .filter(Boolean)
    )
  );
  const safeLanguage = escapeXml(languages[0] || 'en');
  const languagesXml = languages.map((lang) => `<language>${escapeXml(lang)}</language>`).join('\n          ');
  const safeThumbnail = thumbnailPath ? escapeXml(thumbnailPath) : '';

  return `
    <metadata>
      <schema>ADL SCORM</schema>
      <schemaversion>${escapeXml(schemaversion)}</schemaversion>
      <lom xmlns="http://ltsc.ieee.org/xsd/LOM">
        <general>
          ${languagesXml}
          <title>
            <string language="${safeLanguage}">${safeTitle}</string>
          </title>
          <description>
            <string language="${safeLanguage}">${safeDescription}</string>
          </description>
          ${safeThumbnail ? `<keyword><string language="${safeLanguage}">thumbnail:${safeThumbnail}</string></keyword>` : ''}
          ${safeThumbnail ? `<keyword><string language="${safeLanguage}">image:${safeThumbnail}</string></keyword>` : ''}
        </general>
        ${
          safeThumbnail
            ? `<technical>
          <location>${safeThumbnail}</location>
        </technical>`
            : ''
        }
      </lom>
    </metadata>`;
};

const buildManifest = (
  scormVersion: ScormVersion,
  title: string,
  description: string,
  defaultLanguage: string,
  availableLanguages: string[],
  thumbnailPath?: string
): string => {
  const safeTitle = escapeXml(title);
  const schemaversion = scormVersion === '1.2' ? '1.2' : '2004 4th Edition';
  const metadataBlock = buildLomMetadata({
    title,
    description,
    defaultLanguage,
    availableLanguages,
    schemaversion,
    thumbnailPath,
  });

  const imageFileEntry = thumbnailPath ? `\n      <file href="${escapeXml(thumbnailPath)}" />` : '';

  if (scormVersion === '1.2') {
    return `<?xml version="1.0" encoding="UTF-8"?>
<manifest identifier="MANIFEST-FC-SCORM" version="1.0"
  xmlns="http://www.imsproject.org/xsd/imscp_rootv1p1p2"
  xmlns:lom="http://ltsc.ieee.org/xsd/LOM"
  xmlns:adlcp="http://www.adlnet.org/xsd/adlcp_rootv1p2"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsproject.org/xsd/imscp_rootv1p1p2 imscp_rootv1p1p2.xsd
  http://www.adlnet.org/xsd/adlcp_rootv1p2 adlcp_rootv1p2.xsd
  http://ltsc.ieee.org/xsd/LOM lom.xsd">${metadataBlock}
  <organizations default="ORG-1">
    <organization identifier="ORG-1">
      <title>${safeTitle}</title>
      <item identifier="ITEM-1" identifierref="RES-1" isvisible="true">
        <title>${safeTitle}</title>
      </item>
    </organization>
  </organizations>
  <resources>
    <resource identifier="RES-1" type="webcontent" adlcp:scormtype="sco" href="launch.html">
      <file href="launch.html" />
      <file href="scorm-config.json" />${imageFileEntry}
    </resource>
  </resources>
</manifest>`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<manifest identifier="MANIFEST-FC-SCORM" version="1.0"
  xmlns="http://www.imsglobal.org/xsd/imscp_v1p1"
  xmlns:lom="http://ltsc.ieee.org/xsd/LOM"
  xmlns:adlcp="http://www.adlnet.org/xsd/adlcp_v1p3"
  xmlns:imsss="http://www.imsglobal.org/xsd/imsss"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsglobal.org/xsd/imscp_v1p1 imscp_v1p1.xsd
  http://www.adlnet.org/xsd/adlcp_v1p3 adlcp_v1p3.xsd
  http://www.imsglobal.org/xsd/imsss imsss_v1p0.xsd
  http://ltsc.ieee.org/xsd/LOM lom.xsd">${metadataBlock}
  <organizations default="ORG-1">
    <organization identifier="ORG-1">
      <title>${safeTitle}</title>
      <item identifier="ITEM-1" identifierref="RES-1" isvisible="true">
        <title>${safeTitle}</title>
        <adlcp:completionSetByContent>true</adlcp:completionSetByContent>
      </item>
    </organization>
  </organizations>
  <resources>
    <resource identifier="RES-1" type="webcontent" adlcp:scormType="sco" href="launch.html">
      <file href="launch.html" />
      <file href="scorm-config.json" />${imageFileEntry}
    </resource>
  </resources>
</manifest>`;
};

const buildLauncher = (): string => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Fasterclass SCORM Launcher</title>
    <style>
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        background: #000;
      }
      #learnerFrame {
        border: 0;
        width: 100%;
        height: 100%;
        display: block;
      }
    </style>
  </head>
  <body>
    <iframe id="learnerFrame" title="Fasterclass learner"></iframe>
    <script>
      (function () {
        function normalizeText(value) {
          if (value === undefined || value === null) return '';
          return String(value).trim();
        }

        function pickFirstNonEmpty(list) {
          for (var i = 0; i < list.length; i += 1) {
            var value = normalizeText(list[i]);
            if (value) return value;
          }
          return '';
        }

        function parseLearnerName(rawName) {
          var text = normalizeText(rawName);
          if (!text) return { firstName: '', lastName: '' };
          if (text.indexOf(',') >= 0) {
            var partsComma = text.split(',');
            var lastComma = normalizeText(partsComma[0]);
            var firstComma = normalizeText(partsComma.slice(1).join(' '));
            return { firstName: firstComma, lastName: lastComma };
          }
          var parts = text.split(/\\s+/).filter(Boolean);
          if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
          return {
            firstName: parts.slice(0, -1).join(' '),
            lastName: parts[parts.length - 1],
          };
        }

        function clampPercent(value) {
          var numeric = Number(value);
          if (!Number.isFinite(numeric)) return null;
          return Math.max(0, Math.min(100, Math.round(numeric)));
        }

        function normalizeProgressForLms(completionStatus, progressPercent) {
          var value = clampPercent(progressPercent);
          if (completionStatus === 'completed') {
            return value;
          }
          if (value === null || value === 0) {
            return 1;
          }
          return value;
        }

        function relayToScorm2004(api, payload) {
          var progressPercent = normalizeProgressForLms(
            payload.completionStatus || 'incomplete',
            payload.progressPercent
          );
          var progressMeasure = progressPercent === null ? '0.0100' : (progressPercent / 100).toFixed(4);
          var successStatus = payload.completionStatus === 'completed' ? 'passed' : 'unknown';
          if (api.SetValue) {
            api.SetValue('cmi.completion_status', payload.completionStatus || 'incomplete');
            api.SetValue('cmi.progress_measure', progressMeasure);
            api.SetValue('cmi.success_status', successStatus);
            if (payload.details) api.SetValue('cmi.location', String(payload.details).slice(0, 255));
          }
          api.Commit && api.Commit('');
        }

        function terminateScorm2004(api) {
          try {
            api.Commit && api.Commit('');
            api.Terminate && api.Terminate('');
          } catch (e) {}
        }

        function relayToScorm12(api, payload) {
          var lessonStatus = payload.completionStatus === 'completed' ? 'completed' : 'incomplete';
          if (api.LMSSetValue) {
            api.LMSSetValue('cmi.core.lesson_status', lessonStatus);
            if (payload.details) api.LMSSetValue('cmi.core.lesson_location', String(payload.details).slice(0, 255));
          }
          api.LMSCommit && api.LMSCommit('');
        }

        function terminateScorm12(api) {
          try {
            api.LMSCommit && api.LMSCommit('');
            api.LMSFinish && api.LMSFinish('');
          } catch (e) {}
        }

        function ensureScormSession(bridge) {
          if (!bridge || !bridge.api) return false;
          try {
            if (bridge.version === '2004') {
              var result2004 = bridge.api.Initialize && bridge.api.Initialize('');
              return result2004 === 'true' || result2004 === '1';
            }
            var result12 = bridge.api.LMSInitialize && bridge.api.LMSInitialize('');
            return result12 === 'true' || result12 === '1';
          } catch (e) {
            return false;
          }
        }

        function seedInitialScormStatus(config) {
          var bridge = resolveApi(config.scormVersion);
          if (!bridge || !ensureScormSession(bridge)) return;
          var payload = {
            completionStatus: 'incomplete',
            progressPercent: 0,
            details: 'SCORM session opened'
          };
          if (bridge.version === '2004') {
            relayToScorm2004(bridge.api, payload);
          } else {
            relayToScorm12(bridge.api, payload);
          }
        }

        function terminateScormSession(config) {
          var bridge = resolveApi(config.scormVersion);
          if (!bridge) return;
          if (bridge.version === '2004') {
            terminateScorm2004(bridge.api);
          } else {
            terminateScorm12(bridge.api);
          }
        }

        function resolveApi(version) {
          var current = window;
          var depth = 0;
          while (current && depth < 15) {
            try {
              if (version === '2004' && current.API_1484_11) return { version: '2004', api: current.API_1484_11 };
              if (version === '1.2' && current.API) return { version: '1.2', api: current.API };
              if (current.API_1484_11) return { version: '2004', api: current.API_1484_11 };
              if (current.API) return { version: '1.2', api: current.API };
              if (!current.parent || current.parent === current) break;
              current = current.parent;
              depth += 1;
            } catch (e) {
              break;
            }
          }
          return null;
        }

        function readLearnerInfoFromScormRuntime(config) {
          var bridge = resolveApi(config.scormVersion);
          if (!bridge || !bridge.api) return {};

          var result = {};
          try {
            if (bridge.version === '2004') {
              var learnerId2004 = bridge.api.GetValue && bridge.api.GetValue('cmi.learner_id');
              var learnerName2004 = bridge.api.GetValue && bridge.api.GetValue('cmi.learner_name');
              if (normalizeText(learnerId2004)) result.lmsUserId = normalizeText(learnerId2004);
              if (normalizeText(learnerName2004)) result.learnerName = normalizeText(learnerName2004);
            } else {
              var learnerId12 = bridge.api.LMSGetValue && bridge.api.LMSGetValue('cmi.core.student_id');
              var learnerName12 = bridge.api.LMSGetValue && bridge.api.LMSGetValue('cmi.core.student_name');
              if (normalizeText(learnerId12)) result.lmsUserId = normalizeText(learnerId12);
              if (normalizeText(learnerName12)) result.learnerName = normalizeText(learnerName12);
            }
          } catch (e) {
            return result;
          }
          return result;
        }

        function installBridge(config) {
          var lastRelayPayload = {
            completionStatus: 'incomplete',
            progressPercent: 0,
            details: 'SCORM session opened'
          };

          window.addEventListener('message', function (event) {
            var data = event && event.data;
            if (!data) {
              return;
            }
            if (data.type === 'FC_SCORM_RUNTIME_TERMINATE') {
              var bridge = resolveApi(config.scormVersion);
              if (bridge) {
                if (bridge.version === '2004') {
                  relayToScorm2004(bridge.api, lastRelayPayload);
                } else {
                  relayToScorm12(bridge.api, lastRelayPayload);
                }
              }
              terminateScormSession(config);
              return;
            }
            if (data.type !== 'FC_SCORM_RUNTIME_PROGRESS' || !data.payload) {
              return;
            }
            lastRelayPayload = data.payload;
            var bridge = resolveApi(config.scormVersion);
            if (!bridge) {
              return;
            }
            if (bridge.version === '2004') {
              relayToScorm2004(bridge.api, data.payload);
            } else {
              relayToScorm12(bridge.api, data.payload);
            }
          });

          window.addEventListener('pagehide', function () {
            var bridge = resolveApi(config.scormVersion);
            if (bridge) {
              if (bridge.version === '2004') {
                relayToScorm2004(bridge.api, lastRelayPayload);
              } else {
                relayToScorm12(bridge.api, lastRelayPayload);
              }
            }
            terminateScormSession(config);
          });
        }

        (async function () {
          try {
            var response = await fetch('./scorm-config.json');
            var config = await response.json();
            installBridge(config);
            seedInitialScormStatus(config);

            var launchUrl = new URL(config.launchBaseUrl);
            launchUrl.searchParams.set('scormToken', config.token);
            launchUrl.searchParams.set('scormVersion', config.scormVersion);
            if (config.companySlug) {
              launchUrl.searchParams.set('company', config.companySlug);
            }
            var rawSearch = window.location.search || '';
            if (!rawSearch && window.location.hash) {
              var hashValue = window.location.hash.replace(/^#/, '');
              if (hashValue) {
                rawSearch = '?' + hashValue;
              }
            }
            if (!rawSearch) {
              try {
                rawSearch = (window.parent && window.parent.location && window.parent.location.search) || '';
              } catch (e) {
                rawSearch = '';
              }
            }
            var sourceParams = new URLSearchParams(rawSearch);
            sourceParams.forEach(function (value, key) {
              if (key === 'scormToken' || key === 'scormVersion') {
                return;
              }
              launchUrl.searchParams.set(key, value);
            });

            var runtimeInfo = readLearnerInfoFromScormRuntime(config);
            var sourceLearnerName = pickFirstNonEmpty([
              sourceParams.get('learnerName'),
              sourceParams.get('learner_name'),
              sourceParams.get('studentName'),
              sourceParams.get('student_name'),
              sourceParams.get('name'),
              runtimeInfo.learnerName,
            ]);
            var parsedName = parseLearnerName(sourceLearnerName);
            var firstName = pickFirstNonEmpty([
              sourceParams.get('firstName'),
              sourceParams.get('first_name'),
              sourceParams.get('given_name'),
              parsedName.firstName,
            ]);
            var lastName = pickFirstNonEmpty([
              sourceParams.get('lastName'),
              sourceParams.get('last_name'),
              sourceParams.get('family_name'),
              parsedName.lastName,
            ]);
            var lmsUserId = pickFirstNonEmpty([
              sourceParams.get('lmsUserId'),
              sourceParams.get('learnerId'),
              sourceParams.get('learner_id'),
              sourceParams.get('studentId'),
              sourceParams.get('student_id'),
              sourceParams.get('userId'),
              sourceParams.get('user_id'),
              runtimeInfo.lmsUserId,
            ]);
            var locale = pickFirstNonEmpty([
              sourceParams.get('locale'),
              sourceParams.get('language'),
              sourceParams.get('lang'),
              navigator.language,
            ]);

            if (lmsUserId) launchUrl.searchParams.set('lmsUserId', lmsUserId);
            if (firstName) launchUrl.searchParams.set('firstName', firstName);
            if (lastName) launchUrl.searchParams.set('lastName', lastName);
            if (sourceLearnerName) launchUrl.searchParams.set('learnerName', sourceLearnerName);
            if (locale) launchUrl.searchParams.set('locale', locale);

            var frame = document.getElementById('learnerFrame');
            frame.src = launchUrl.toString();
          } catch (error) {
            document.body.innerHTML = '<pre>SCORM launch failed. Check scorm-config.json and launch URL.</pre>';
          }
        })();
      })();
    </script>
  </body>
</html>`;

const CourseScormExport: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creatingExport, setCreatingExport] = useState(false);
  const [updatingExportStatusId, setUpdatingExportStatusId] = useState<string>('');
  const [enrollmentSavingId, setEnrollmentSavingId] = useState<string>('');
  const [redownloadingExportId, setRedownloadingExportId] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [course, setCourse] = useState<CourseResponse | null>(null);
  const [exportsList, setExportsList] = useState<ScormExportItem[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  /** Brouillon formulaire inscription libre par export (aligné sur exportsList au chargement). */
  const [exportEnrollmentDraft, setExportEnrollmentDraft] = useState<
    Record<string, { freeEnrollment: boolean; emailOptional: boolean; enrollmentGroupId: string }>
  >({});

  const [clientId, setClientId] = useState('');
  const [scormVersion, setScormVersion] = useState<ScormVersion>('2004');
  const [tokenExpiresAt, setTokenExpiresAt] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  const token = localStorage.getItem('admin_token');
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const learnerScormLoginUrl = process.env.REACT_APP_SCORM_LEARNER_LOGIN_URL || '';

  const courseTitle = useMemo(() => {
    if (!course) return 'course';
    return resolveTitle(course.title, course.defaultLanguage);
  }, [course]);
  const adminCourseName = useMemo(() => {
    if (!course) return 'Formation';
    return getAdminCourseName(course);
  }, [course]);

  const courseDescription = useMemo(() => {
    if (!course) return '';
    return getLocalized(course.description, course.defaultLanguage || 'en', '');
  }, [course]);

  const courseLanguage = useMemo(() => {
    if (!course) return 'en';
    return course.defaultLanguage || course.availableLanguages?.[0] || 'en';
  }, [course]);

  const getImageUrl = useCallback(
    (imageUrl?: string): string => {
      if (!imageUrl) return '';
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      if (imageUrl.startsWith('/')) {
        const base = (apiBaseUrl || '').replace(/\/api$/, '');
        if (imageUrl.startsWith('/uploads/')) {
          return `${apiBaseUrl}${imageUrl}`;
        }
        return `${base}${imageUrl}`;
      }
      return imageUrl;
    },
    [apiBaseUrl]
  );

  const inferImageExtension = (contentType?: string, sourceUrl?: string): string => {
    const ct = (contentType || '').toLowerCase();
    if (ct.includes('png')) return 'png';
    if (ct.includes('jpeg') || ct.includes('jpg')) return 'jpg';
    if (ct.includes('webp')) return 'webp';
    if (ct.includes('gif')) return 'gif';
    if (ct.includes('svg')) return 'svg';
    const cleanUrl = (sourceUrl || '').split('?')[0].split('#')[0];
    const match = cleanUrl.match(/\.([a-zA-Z0-9]{2,5})$/);
    return match?.[1]?.toLowerCase() || 'jpg';
  };

  const fetchScormData = useCallback(async () => {
    if (!courseId) return;

    setLoading(true);
    setError('');
    try {
      const [courseRes, exportsRes, companiesRes, groupsRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/courses/${courseId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiBaseUrl}/scorm/courses/${courseId}/exports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiBaseUrl}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiBaseUrl}/groups/course/${courseId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setCourse(courseRes.data);
      const exportsPayload = exportsRes.data?.exports || [];
      setExportsList(exportsPayload);
      const drafts: Record<string, { freeEnrollment: boolean; emailOptional: boolean; enrollmentGroupId: string }> =
        {};
      exportsPayload.forEach((e: ScormExportItem) => {
        drafts[e.id] = {
          freeEnrollment: Boolean(e.freeEnrollment),
          emailOptional: Boolean(e.emailOptional),
          enrollmentGroupId: e.enrollmentGroupId || '',
        };
      });
      setExportEnrollmentDraft(drafts);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setCompanies(companiesRes.data?.companies || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de charger la configuration SCORM');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, courseId, token]);

  useEffect(() => {
    void fetchScormData();
  }, [fetchScormData]);

  useEffect(() => {
    if (!courseId || !courseTitle || clientId.trim()) {
      return;
    }
    const generated = `client-${toSlug(courseTitle)}-${courseId.slice(-6)}`;
    setClientId(generated);
  }, [courseId, courseTitle, clientId]);

  const handleSaveExportEnrollment = async (exportId: string) => {
    if (!courseId) return;
    const draft = exportEnrollmentDraft[exportId];
    if (!draft) return;
    setEnrollmentSavingId(exportId);
    setError('');
    setSuccess('');
    try {
      if (draft.freeEnrollment && !draft.enrollmentGroupId) {
        setError('Sélectionnez un groupe lorsque l\'inscription libre est activée pour cet export.');
        return;
      }
      await axios.patch(
        `${apiBaseUrl}/scorm/courses/${courseId}/exports/${exportId}/enrollment`,
        {
          freeEnrollment: draft.freeEnrollment,
          emailOptional: draft.freeEnrollment ? draft.emailOptional : false,
          enrollmentGroupId: draft.freeEnrollment ? draft.enrollmentGroupId : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Options d\'inscription enregistrées pour cet export.');
      await fetchScormData();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible d\'enregistrer les options d\'inscription.');
    } finally {
      setEnrollmentSavingId('');
    }
  };

  const handleRedownloadPackage = async (item: ScormExportItem) => {
    if (!courseId) return;
    if (!learnerScormLoginUrl.trim()) {
      setError("La variable d'environnement REACT_APP_SCORM_LEARNER_LOGIN_URL est obligatoire");
      return;
    }
    setRedownloadingExportId(item.id);
    setError('');
    setSuccess('');
    try {
      const res = await axios.get<ScormZipPayload>(
        `${apiBaseUrl}/scorm/courses/${courseId}/exports/${item.id}/package-credentials`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await downloadExportZip(res.data);
      setSuccess('Package SCORM téléchargé.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de retélécharger le package SCORM');
    } finally {
      setRedownloadingExportId('');
    }
  };

  const downloadExportZip = async (packagePayload: ScormZipPayload) => {
    const zip = new JSZip();
    let thumbnailPath = '';
    if (course?.image) {
      try {
        const imageUrl = getImageUrl(course.image);
        if (imageUrl) {
          const imageResponse = await axios.get(imageUrl, {
            responseType: 'blob',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          const extension = inferImageExtension(imageResponse.headers?.['content-type'], imageUrl);
          thumbnailPath = `assets/course-image.${extension}`;
          zip.file(thumbnailPath, imageResponse.data);
        }
      } catch (error) {
        // Best effort only: some LMS might still use metadata without image file.
        // eslint-disable-next-line no-console
        console.warn('SCORM export: unable to bundle course image', error);
      }
    }

    zip.file(
      'imsmanifest.xml',
      buildManifest(
        packagePayload.scormVersion,
        courseTitle,
        courseDescription || courseTitle,
        courseLanguage,
        course?.availableLanguages || [],
        thumbnailPath || undefined
      )
    );
    zip.file('launch.html', buildLauncher());
    zip.file(
      'scorm-config.json',
      JSON.stringify(
        {
          token: packagePayload.token,
          courseId,
          clientId: packagePayload.clientId,
          scormVersion: packagePayload.scormVersion,
          exportVersion: packagePayload.exportVersion,
          launchBaseUrl: learnerScormLoginUrl,
          tokenExpiresAt: packagePayload.tokenExpiresAt || null,
          companySlug: packagePayload.companySlug || null,
        },
        null,
        2
      )
    );
    zip.file(
      'README.txt',
      `Fasterclass SCORM package
- Course: ${courseTitle}
- Description: ${courseDescription || '(none)'}
- Export version: ${packagePayload.exportVersion}
- SCORM version: ${packagePayload.scormVersion}
- Client ID: ${packagePayload.clientId}
${packagePayload.companySlug ? `- Company slug: ${packagePayload.companySlug} (?company=${packagePayload.companySlug} on login URL)` : ''}
${thumbnailPath ? `- Thumbnail candidate: ${thumbnailPath}` : '- Thumbnail candidate: none'}

This package launches the learner SCORM URL from admin environment configuration and forwards the SCORM token.`
    );

    const blob = await zip.generateAsync({ type: 'blob' });
    const filename = `fasterclass-${courseId}-${packagePayload.exportVersion}-scorm-${packagePayload.scormVersion}.zip`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleCreateAndDownload = async () => {
    if (!courseId) return;
    if (!clientId.trim()) {
      setError('Le clientId est obligatoire');
      return;
    }
    if (!learnerScormLoginUrl.trim()) {
      setError("La variable d'environnement REACT_APP_SCORM_LEARNER_LOGIN_URL est obligatoire");
      return;
    }
    if (!companyId) {
      setError("L'entreprise assignee est obligatoire");
      return;
    }

    setCreatingExport(true);
    setError('');
    setSuccess('');
    try {
      const payload: { clientId: string; scormVersion: ScormVersion; tokenExpiresAt?: string; companyId: string } = {
        clientId: clientId.trim(),
        scormVersion,
        companyId,
      };
      if (tokenExpiresAt) {
        payload.tokenExpiresAt = new Date(tokenExpiresAt).toISOString();
      }
      const response = await axios.post<CreateExportResponse>(
        `${apiBaseUrl}/scorm/courses/${courseId}/exports`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await downloadExportZip(response.data.export);
      setSuccess('Export SCORM créé et téléchargé');
      await fetchScormData();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Impossible de créer l'export SCORM");
    } finally {
      setCreatingExport(false);
    }
  };

  const handleToggleExportStatus = async (exportId: string, nextActive: boolean) => {
    if (!courseId) return;

    setUpdatingExportStatusId(exportId);
    setError('');
    try {
      await axios.patch(
        `${apiBaseUrl}/scorm/courses/${courseId}/exports/${exportId}/status`,
        { active: nextActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExportsList((prev) =>
        prev.map((item) => (item.id === exportId ? { ...item, active: nextActive } : item))
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || "Impossible de mettre à jour le statut de l'export");
    } finally {
      setUpdatingExportStatusId('');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="320px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold">
          Export SCORM
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Formation: {adminCourseName}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Créer un export SCORM
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Client ID"
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
                fullWidth
                required
                helperText="Nous permet d'identifier a qui a été donné ce scorm exemple : limagrain-feedback-300625"
              />
              <TextField
                label="Version SCORM"
                value={scormVersion}
                onChange={(event) => setScormVersion(event.target.value as ScormVersion)}
                select
                fullWidth
              >
                <MenuItem value="1.2">SCORM 1.2</MenuItem>
                <MenuItem value="2004">SCORM 2004</MenuItem>
              </TextField>
              <TextField
                label="Expiration du token (optionnel)"
                type="datetime-local"
                value={tokenExpiresAt}
                onChange={(event) => setTokenExpiresAt(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Entreprise assignee"
                value={companyId}
                onChange={(event) => setCompanyId(event.target.value)}
                select
                fullWidth
                required
              >
                <MenuItem value="" disabled>
                  Selectionner une entreprise
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name} ({company.slug})
                  </MenuItem>
                ))}
              </TextField>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleCreateAndDownload}
                  disabled={creatingExport}
                >
                  {creatingExport ? 'Création du package...' : 'Créer et télécharger le package SCORM (.zip)'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Exports existants
            </Typography>
            <Stack spacing={1.5}>
              {exportsList.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Aucun export SCORM généré pour cette formation.
                </Typography>
              )}
              {exportsList.map((item) => {
                const draft =
                  exportEnrollmentDraft[item.id] ?? {
                    freeEnrollment: Boolean(item.freeEnrollment),
                    emailOptional: Boolean(item.emailOptional),
                    enrollmentGroupId: item.enrollmentGroupId || '',
                  };
                return (
                  <Box
                    key={item.id}
                    border="1px solid"
                    borderColor="divider"
                    borderRadius={1}
                    px={2}
                    py={1.5}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {item.exportVersion} - SCORM {item.scormVersion}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          clientId: {item.clientId}
                          {item.company?.name ? ` | entreprise: ${item.company.name}` : ''}
                          {' | '}apprenants uniques: {item.uniqueLearnersCount || 0}
                          {' | '}créé le {new Date(item.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={item.company?.name || 'Entreprise non assignee'}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Apprenants: ${item.uniqueLearnersCount || 0}`}
                        />
                        <Tooltip title="Retélécharger le package SCORM (.zip)">
                          <IconButton
                            size="small"
                            disabled={redownloadingExportId === item.id}
                            onClick={() => void handleRedownloadPackage(item)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Voir les connexions">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/courses/${courseId}/scorm/${item.id}/connections`)}
                          >
                            <GroupsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Chip size="small" color={item.active ? 'success' : 'default'} label={item.active ? 'Actif' : 'Inactif'} />
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={(
                            <Switch
                              size="small"
                              checked={item.active}
                              disabled={updatingExportStatusId === item.id}
                              onChange={(event) => {
                                void handleToggleExportStatus(item.id, event.target.checked);
                              }}
                            />
                          )}
                          label=""
                        />
                        {item.tokenExpiresAt && (
                          <Chip size="small" variant="outlined" label={`Expire: ${toDatetimeLocal(item.tokenExpiresAt)}`} />
                        )}
                      </Box>
                    </Box>

                    <Stack spacing={1} mt={2}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Options d&apos;inscription (cet export)
                      </Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
                        <FormControlLabel
                          control={(
                            <Switch
                              size="small"
                              checked={draft.freeEnrollment}
                              onChange={(event) => {
                                const freeEnrollment = event.target.checked;
                                setExportEnrollmentDraft((prev) => {
                                  const cur = prev[item.id] ?? {
                                    freeEnrollment: Boolean(item.freeEnrollment),
                                    emailOptional: Boolean(item.emailOptional),
                                    enrollmentGroupId: item.enrollmentGroupId || '',
                                  };
                                  return {
                                    ...prev,
                                    [item.id]: {
                                      ...cur,
                                      freeEnrollment,
                                      emailOptional: freeEnrollment ? cur.emailOptional : false,
                                      enrollmentGroupId: freeEnrollment ? cur.enrollmentGroupId : '',
                                    },
                                  };
                                });
                              }}
                            />
                          )}
                          label="Inscription libre"
                        />
                        <FormControlLabel
                          control={(
                            <Switch
                              size="small"
                              checked={draft.emailOptional}
                              disabled={!draft.freeEnrollment}
                              onChange={(event) => {
                                setExportEnrollmentDraft((prev) => {
                                  const cur = prev[item.id] ?? {
                                    freeEnrollment: Boolean(item.freeEnrollment),
                                    emailOptional: Boolean(item.emailOptional),
                                    enrollmentGroupId: item.enrollmentGroupId || '',
                                  };
                                  return {
                                    ...prev,
                                    [item.id]: {
                                      ...cur,
                                      emailOptional: event.target.checked,
                                    },
                                  };
                                });
                              }}
                            />
                          )}
                          label="Email optionnel"
                        />
                        {draft.freeEnrollment && (
                          <TextField
                            select
                            size="small"
                            label="Groupe d'inscription"
                            value={draft.enrollmentGroupId}
                            onChange={(event) => {
                              setExportEnrollmentDraft((prev) => {
                                const cur = prev[item.id] ?? {
                                  freeEnrollment: Boolean(item.freeEnrollment),
                                  emailOptional: Boolean(item.emailOptional),
                                  enrollmentGroupId: item.enrollmentGroupId || '',
                                };
                                return {
                                  ...prev,
                                  [item.id]: {
                                    ...cur,
                                    enrollmentGroupId: event.target.value,
                                  },
                                };
                              });
                            }}
                            sx={{ minWidth: 240 }}
                          >
                            <MenuItem value="">
                              <em>Sélectionner un groupe</em>
                            </MenuItem>
                            {groups.map((g) => (
                              <MenuItem key={g.id} value={String(g.id)}>
                                {g.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SaveIcon />}
                          disabled={enrollmentSavingId === item.id}
                          onClick={() => void handleSaveExportEnrollment(item.id)}
                        >
                          {enrollmentSavingId === item.id ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default CourseScormExport;
